import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { IoTimeOutline } from "react-icons/io5";

import { useDisableScroll } from "../../hooks/useDisableScroll";

import {
  DetailEventContainer,
  EventTitleContainer,
  ReportContainer,
} from "../mypage/scheduler/component/styled/scheduler";

import {
  isOutsideCmpny,
  isEventCategory,
  showEventTime,
} from "../mypage/scheduler/component/function/common";

import Info from "./shareReport/info";
import UseApp from "../../common/useApp";

// 무거운 탭/뷰어는 lazy 로딩 (초기 번들 감소)
const Gallery = React.lazy(() =>
  import("../mypage/scheduler/component/scheduler/event/gallery")
);
const Members = React.lazy(() =>
  import("../mypage/scheduler/component/scheduler/event/members")
);
const Comment = React.lazy(() =>
  import("../mypage/scheduler/component/scheduler/event/comment")
);
const Report = React.lazy(() =>
  import("../mypage/scheduler/component/scheduler/event/report")
);
const MediaViewer = React.lazy(() => import("../../common/mediaViewer"));

const CDN_HOST = "https://cdn.1472.ai/";

function formatKoreanDate(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}

function normalizeHex(v) {
  if (!v) return null;
  const s = String(v).trim();
  return s.startsWith("#") ? s.slice(1) : s;
}

function buildOriginUrlFromPath(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path; // 이미 풀 URL이면 그대로
  const p = path.startsWith("/") ? path.slice(1) : path;
  return `${CDN_HOST}${p}`;
}

function withResize(url) {
  if (!url) return "";
  return url.includes("?") ? `${url}&resize=true` : `${url}?resize=true`;
}

const ShareReport = () => {
  useDisableScroll();

  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ query: "(max-width:767px)" });

  const user = useSelector((state) => state?.user?.data?.[0]);
  const company = useSelector((state) => state?.company?.data);

  const [noValue, setNoValue] = useState(null);
  const [scheduleNo, setScheduleNo] = useState(null);

  const [eventInfo, setEventInfo] = useState(null);
  const [isSchedule, setIsSchedule] = useState(false);

  const [active, setActive] = useState(1);
  const [todos, setTodos] = useState([]);

  const [comments, setComments] = useState([]);
  const [originImages, setOriginImages] = useState([]);

  const [isDetail, setIsDetail] = useState(false);
  const [imageOrder, setImageOrder] = useState([]);

  const [isUpload, setIsUpload] = useState(false);
  const [isUseApp, setIsUseApp] = useState(true);

  // 로딩 상태 분리
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [loadingExtras, setLoadingExtras] = useState(false);

  const clickGallery = useCallback(() => {
    setIsUpload(false);
    setActive(2);
  }, []);

  const getColor = useCallback((gbn, color) => {
    const colorMap = { C: "0074ca", G: "F46F02" };
    return colorMap[gbn] || normalizeHex(color) || "333";
  }, []);

  // query 변경 반응
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setNoValue(urlParams.get("no"));
  }, [location.search]);

  // 서버에서 주입된 데이터가 있으면 네트워크 스킵
  useEffect(() => {
    const injected = typeof window !== "undefined" ? window.__SHARE_REPORT__ : null;
    if (injected?.eventInfo) {
      setEventInfo(injected.eventInfo);
      setIsSchedule((injected.eventInfo?.dday || "") !== "Y");

      // 주입된 이미지/댓글도 동일하게 썸네일 가공
      const injectedImages = (injected.originImages || []).map((p) => {
        const originUrl =
          p.originUrl || p.url || p.src || buildOriginUrlFromPath(p.path);
        const thumbUrl = p.thumbUrl || withResize(originUrl);

        // 자식들이 path로 src를 쓰더라도 썸네일을 쓰게 path를 thumbUrl로 덮음
        return {
          ...p,
          originUrl,
          thumbUrl,
          originalPath: p.path,
          path: thumbUrl,
        };
      });

      setOriginImages(injectedImages);
      setComments(injected.comments || []);
    }
  }, []);

  // 일정번호 복호화
  const getScheduleNo = useCallback(
    async (encrypted) => {
      try {
        const res = await axios.post("/etc/decrypt", { encrypted });
        if (res.status === 200) {
          const dec = res.data.decrypted;
          setScheduleNo(dec);
          return dec;
        }
        return null;
      } catch (e) {
        toast.error("잘못된 주소입니다.", { toastId: "no-access" });
        navigate("/");
        return null;
      }
    },
    [navigate]
  );

  // 댓글 조회
  const getComment = useCallback(async (sn) => {
    if (!sn) return;
    try {
      const res = await axios.post("/api/scheduleComment/get", { scheduleNo: sn });
      if (res.status === 200) setComments(res.data || []);
    } catch (e) {
      setComments([]);
    }
  }, []);

  // 이미지 조회 (여기서 썸네일/원본 URL 생성)
  const getImages = useCallback(async (sn) => {
    if (!sn) return;
    try {
      const res = await axios.post("/api/photoList", { refeNo: sn });
      const list = (res.data || []).map((p) => {
        const originUrl = buildOriginUrlFromPath(p.path);
        const thumbUrl = withResize(originUrl);

        return {
          ...p,
          originUrl,
          thumbUrl,
          originalPath: p.path, // 원본 path 보관
          path: thumbUrl, // 썸네일용으로 강제
        };
      });

      setOriginImages(list);
    } catch (e) {
      setOriginImages([]);
    }
  }, []);

  // 일정 조회 (event 먼저, extras 병렬)
  const getEvent = useCallback(
    async (encryptedNo) => {
      setLoadingEvent(true);
      setLoadingExtras(false);

      const sn = await getScheduleNo(encryptedNo);
      if (!sn) {
        setLoadingEvent(false);
        return;
      }

      try {
        const res = await axios.post("/api/scheduleList", { scheduleNo: sn });

        if (res.status !== 200 || !res.data?.length) {
          toast.error("존재하지 않는 일정입니다.", { toastId: "no-access" });
          navigate("/");
          setLoadingEvent(false);
          return;
        }

        const data = res.data[0];

        // 화면 먼저 띄우기
        setEventInfo(data);
        setIsSchedule(data.dday !== "Y");
        setLoadingEvent(false);

        // 댓글/이미지 병렬 + await로 막지 않음
        setLoadingExtras(true);
        Promise.all([getComment(sn), getImages(sn)]).finally(() => {
          setLoadingExtras(false);
        });
      } catch (error) {
        console.error("일정 조회 오류", error);
        setLoadingEvent(false);
      }
    },
    [getScheduleNo, getComment, getImages, navigate]
  );

  useEffect(() => {
    if (!noValue) return;
    const injected = typeof window !== "undefined" ? window.__SHARE_REPORT__ : null;
    if (injected?.eventInfo) return;
    getEvent(noValue);
  }, [noValue, getEvent]);

  const renderScheduleInfo = useCallback(() => {
    if (!isSchedule || !eventInfo) return null;

    const { gbn, managerNm, workerList, orgNm } = eventInfo;
    const isPrivate = gbn === "P";

    return (
      <span>
        {isPrivate && managerNm && `${managerNm}님의 `}
        {isPrivate && workerList ? "공유" : isEventCategory(gbn)}
        일정
        {!isPrivate && ` : ${orgNm}`}
      </span>
    );
  }, [eventInfo, isSchedule]);

  const titleDotBg = useMemo(() => {
    if (!eventInfo) return "#333";
    if (user && company && isOutsideCmpny(eventInfo, company)) {
      return "linear-gradient(rgb(0, 116, 202), rgb(237, 85, 131))";
    }
    return `#${getColor(eventInfo?.gbn, eventInfo?.color)}`;
  }, [eventInfo, user, company, getColor]);

  const renderComponent = () => {
    return (
      <Suspense fallback={<div style={{ padding: 16 }}>불러오는 중…</div>}>
        {active === 2 ? (
          <Gallery
            eventInfo={eventInfo}
            setImageOrder={setImageOrder}
            setIsDetail={setIsDetail}
            isGallery={true}
            originImages={originImages}
            // 기존 시그니처 유지
            getImages={() => scheduleNo && getImages(scheduleNo)}
            clickGallery={clickGallery}
            isUpload={isUpload}
          />
        ) : active === 3 ? (
          <Comment
            eventInfo={eventInfo}
            comments={comments}
            getComment={getComment}
            isShared={true}
            setImageOrder={setImageOrder}   // 추가
            setIsDetail={setIsDetail}       // 추가
            getImages={getImages}           // (선택) 댓글 ↔ 갤러리 연동 시
          />
        ) : active === 4 ? (
          <Members eventInfo={eventInfo} />
        ) : (
          <Report
            eventInfo={eventInfo}
            isSchedule={isSchedule}
            setTodos={setTodos}
            todos={todos}
            setIsDetail={setIsDetail}
            setImageOrder={setImageOrder}
            isGallery={true}
            setActive={setActive}
            originImages={originImages}
            clickGallery={clickGallery}
            isUpload={isUpload}
            comments={comments}
          />
        )}
      </Suspense>
    );
  };

  // MediaViewer에는 “원본” 강제 주입 (자식에서 thumb(path)로 넘겨도 여기서 원복)
  const viewerFiles = useMemo(() => {
    const files = Array.isArray(imageOrder) ? imageOrder : [];
    return files.map((f) => {
      const originUrl =
        f.originUrl ||
        f.origin ||
        f.urlOrigin ||
        (f.originalPath ? buildOriginUrlFromPath(f.originalPath) : null) ||
        (f.path ? buildOriginUrlFromPath(f.path) : null);

      // MediaViewer가 어떤 키를 쓰든 원본으로 최대한 호환되게 주입
      return {
        ...f,
        originUrl,
        originalPath: f.originalPath || f.path,
        path: originUrl || f.path,
        url: originUrl || f.url,
        src: originUrl || f.src,
      };
    });
  }, [imageOrder]);

  return (
    <DetailEventContainer>
      {isUseApp && isMobile && (
        <UseApp
          setIsUseApp={setIsUseApp}
          message={{
            title: "한 번의 클릭으로\n더 편리하게!",
            info: "앱 설치로 빠르고 간편하게 이용하세요",
          }}
        />
      )}

      <Suspense fallback={null}>
        {isDetail && viewerFiles.length > 0 && (
          <MediaViewer files={viewerFiles} setIsOpen={setIsDetail} isComment />
        )}
      </Suspense>

      <ReportContainer>
        <Info active={active} setActive={setActive} />

        <div className="content">
          {active === 1 && eventInfo && (
            <EventTitleContainer className="view-1 detail">
              <div className="dot" style={{ background: titleDotBg }} />
              <ul>
                <li className="title">
                  {eventInfo?.icon && (
                    <img
                      src={`/img/scheduler/scheduler_icon_${eventInfo?.icon}`}
                      alt="event icon"
                    />
                  )}
                  <span className={eventInfo?.completeYn === "Y" ? "complete" : ""}>
                    {eventInfo?.title}
                  </span>
                </li>

                <li>{renderScheduleInfo()}</li>

                <li className="date">
                  <i>
                    <IoTimeOutline />
                  </i>
                  {isSchedule
                    ? showEventTime(eventInfo?.startDt, eventInfo?.endDt)
                    : formatKoreanDate(eventInfo?.startDt)}
                </li>

                {loadingExtras && (
                  <li style={{ fontSize: 12, opacity: 0.6 }}>
                    사진/댓글 불러오는 중…
                  </li>
                )}
              </ul>
            </EventTitleContainer>
          )}

          {loadingEvent && !eventInfo ? (
            <div style={{ padding: 16 }}>일정 불러오는 중…</div>
          ) : (
            renderComponent()
          )}
        </div>
      </ReportContainer>
    </DetailEventContainer>
  );
};

export default ShareReport;
